import type {PathOp} from '../painter/types'

const COMMAND_RE = /([MLCZ])/g
const NUMBER_RE = /-?(?:\d+\.\d*|\.\d+|\d+)/g
const ARG_COUNT: Record<string, number> = {M: 2, L: 2, C: 6, Z: 0}

const cache = new Map<string, readonly PathOp[]>()

/**
 * Parse an SVG path `d` string into normalised `PathOp[]`. Supports
 * only absolute `M`, `L`, `C`, `Z` (the only commands present in
 * GLYPHS_A/B). Throws on anything else so a future glyph regen with
 * arcs/quadratics/relative commands fails loudly. Memoised by the
 * exact input string so each glyph parses at most once per run.
 */
export function parseGlyphOps(d: string): readonly PathOp[] {
    const cached = cache.get(d)
    if (cached) return cached
    const ops = parseUncached(d)
    cache.set(d, ops)
    return ops
}

function parseUncached(d: string): readonly PathOp[] {
    const trimmed = d.trim()
    if (trimmed === '') return Object.freeze([])

    const badCmd = trimmed.match(/[a-zQTSAHVqtsahv]/)
    if (badCmd) {
        throw new Error(
            `unsupported SVG path command "${badCmd[0]}" in "${d}" (only absolute M/L/C/Z allowed)`
        )
    }

    const raw = trimmed.split(COMMAND_RE).map(s => s.trim())
    const tokens = raw.slice(1)
    if (tokens.length === 0 || !(tokens[0] in ARG_COUNT)) {
        throw new Error(`unsupported SVG path command in "${d}" (path must start with M)`)
    }

    const ops: PathOp[] = []
    for (let i = 0; i < tokens.length; i += 2) {
        const cmd = tokens[i]
        const argsRaw = tokens[i + 1] ?? ''
        if (!(cmd in ARG_COUNT)) {
            throw new Error(`unsupported SVG path command "${cmd}" in "${d}"`)
        }
        const expected = ARG_COUNT[cmd]
        if (expected === 0) {
            ops.push({op: 'h'})
            continue
        }
        const nums = (argsRaw.match(NUMBER_RE) ?? []).map(Number)
        if (nums.length !== expected) {
            throw new Error(
                `command "${cmd}" expected ${expected} numbers, got ${nums.length} in "${d}"`
            )
        }
        if (cmd === 'M') ops.push({op: 'm', x: nums[0], y: nums[1]})
        else if (cmd === 'L') ops.push({op: 'l', x: nums[0], y: nums[1]})
        else if (cmd === 'C') {
            ops.push({
                op: 'c',
                c1x: nums[0],
                c1y: nums[1],
                c2x: nums[2],
                c2y: nums[3],
                x: nums[4],
                y: nums[5]
            })
        }
    }
    return Object.freeze(ops)
}
